import { UserRole, getUserRoleString, getUserRoleFromString } from '@/types/api';

/**
 * Role Normalization Utility
 * 
 * Handles conversion between different role formats:
 * - String roles (e.g., "Admin", "Agent", "EndUser")
 * - Numeric roles (e.g., 0, 1, 2, 3)
 * - Mixed types from API responses
 * 
 * Ensures backward compatibility and consistent role handling
 * across the entire application.
 */

export interface RoleInfo {
  name: string;
  value: UserRole;
  displayName: string;
  level: number;
  permissions: string[];
}

/**
 * Role hierarchy and permissions mapping
 */
export const ROLE_HIERARCHY: Record<UserRole, RoleInfo> = {
  [UserRole.EndUser]: {
    name: 'EndUser',
    value: UserRole.EndUser,
    displayName: 'End User',
    level: 0,
    permissions: ['view_own_tickets', 'create_tickets', 'reply_to_tickets']
  },
  [UserRole.Agent]: {
    name: 'Agent',
    value: UserRole.Agent,
    displayName: 'Agent',
    level: 1,
    permissions: [
      'view_assigned_tickets', 'update_tickets', 'assign_tickets',
      'view_knowledge_base', 'create_internal_notes'
    ]
  },
  [UserRole.Manager]: {
    name: 'Manager',
    value: UserRole.Manager,
    displayName: 'Manager',
    level: 2,
    permissions: [
      'view_all_tickets', 'manage_agents', 'view_reports',
      'manage_sla_policies', 'escalate_tickets'
    ]
  },
  [UserRole.Admin]: {
    name: 'Admin',
    value: UserRole.Admin,
    displayName: 'Administrator',
    level: 3,
    permissions: [
      'full_system_access', 'manage_users', 'system_configuration',
      'view_all_data', 'manage_integrations', 'system_administration'
    ]
  }
};

/**
 * Normalize any role input to UserRole enum
 */
export function normalizeRole(role: unknown): UserRole {
  // Handle null/undefined
  if (role === null || role === undefined) {
    console.warn('Role normalization: null/undefined role, defaulting to EndUser');
    return UserRole.EndUser;
  }

  // Handle numeric roles (API returns numbers)
  if (typeof role === 'number') {
    // Validate numeric role
    if (role >= 0 && role <= 3 && Number.isInteger(role)) {
      return role as UserRole;
    } else {
      console.warn(`Role normalization: invalid numeric role ${role}, defaulting to EndUser`);
      return UserRole.EndUser;
    }
  }

  // Handle string roles
  if (typeof role === 'string') {
    try {
      return getUserRoleFromString(role);
    } catch (error) {
      console.warn(`Role normalization: failed to parse string role "${role}", defaulting to EndUser`);
      return UserRole.EndUser;
    }
  }

  // Handle object with role property
  if (typeof role === 'object' && role !== null) {
    const roleObj = role as any;
    
    // Check for common role properties
    if ('role' in roleObj) {
      return normalizeRole(roleObj.role);
    }
    if ('userRole' in roleObj) {
      return normalizeRole(roleObj.userRole);
    }
    if ('type' in roleObj) {
      return normalizeRole(roleObj.type);
    }
  }

  console.warn(`Role normalization: unknown role type ${typeof role}, defaulting to EndUser`, role);
  return UserRole.EndUser;
}

/**
 * Check if a role has specific permissions
 */
export function hasPermission(role: unknown, permission: string): boolean {
  const normalizedRole = normalizeRole(role);
  const roleInfo = ROLE_HIERARCHY[normalizedRole];
  
  // Admins have all permissions
  if (normalizedRole === UserRole.Admin) {
    return true;
  }
  
  return roleInfo.permissions.includes(permission);
}

/**
 * Check if a role has a minimum level
 */
export function hasMinimumRole(role: unknown, minimumRole: UserRole): boolean {
  const normalizedRole = normalizeRole(role);
  const userLevel = ROLE_HIERARCHY[normalizedRole].level;
  const minimumLevel = ROLE_HIERARCHY[minimumRole].level;
  
  return userLevel >= minimumLevel;
}

/**
 * Check if user is admin (handles all formats)
 */
export function isAdmin(role: unknown): boolean {
  const normalizedRole = normalizeRole(role);
  return normalizedRole === UserRole.Admin;
}

/**
 * Check if user is agent or higher (Agent, Manager, Admin)
 */
export function isAgent(role: unknown): boolean {
  return hasMinimumRole(role, UserRole.Agent);
}

/**
 * Check if user is manager or higher (Manager, Admin)
 */
export function isManager(role: unknown): boolean {
  return hasMinimumRole(role, UserRole.Manager);
}

/**
 * Check if user is end user only
 */
export function isEndUser(role: unknown): boolean {
  const normalizedRole = normalizeRole(role);
  return normalizedRole === UserRole.EndUser;
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role: unknown): string {
  const normalizedRole = normalizeRole(role);
  return ROLE_HIERARCHY[normalizedRole].displayName;
}

/**
 * Get role name for API calls
 */
export function getRoleName(role: unknown): string {
  const normalizedRole = normalizeRole(role);
  return ROLE_HIERARCHY[normalizedRole].name;
}

/**
 * Check if one role has higher level than another
 */
export function isHigherRole(role1: unknown, role2: unknown): boolean {
  const normalized1 = normalizeRole(role1);
  const normalized2 = normalizeRole(role2);
  
  return ROLE_HIERARCHY[normalized1].level > ROLE_HIERARCHY[normalized2].level;
}

/**
 * Get all roles at or below a certain level
 */
export function getRolesAtOrBelow(role: unknown): UserRole[] {
  const normalizedRole = normalizeRole(role);
  const userLevel = ROLE_HIERARCHY[normalizedRole].level;
  
  return Object.values(UserRole)
    .filter(r => typeof r === 'number')
    .filter(r => ROLE_HIERARCHY[r as UserRole].level <= userLevel) as UserRole[];
}

/**
 * Test helper: Create test user with normalized role
 */
export function createTestUser(role: unknown = UserRole.EndUser) {
  const normalizedRole = normalizeRole(role);
  const roleInfo = ROLE_HIERARCHY[normalizedRole];
  
  return {
    id: 'test-user-id',
    email: 'test@example.com',
    firstName: 'Test',
    lastName: 'User',
    fullName: 'Test User',
    role: normalizedRole,
    roleName: roleInfo.name,
    roleDisplayName: roleInfo.displayName,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * Validate role data from API response
 */
export function validateRoleData(userData: any): {
  isValid: boolean;
  normalizedRole: UserRole;
  issues: string[];
} {
  const issues: string[] = [];
  
  if (!userData) {
    issues.push('User data is null or undefined');
    return { isValid: false, normalizedRole: UserRole.EndUser, issues };
  }
  
  let normalizedRole: UserRole;
  
  try {
    normalizedRole = normalizeRole(userData.role);
    
    // Check for common API inconsistencies
    if (userData.role !== undefined && typeof userData.role === 'string') {
      const stringRole = userData.role.toLowerCase();
      if (!['admin', 'manager', 'agent', 'enduser', 'user'].includes(stringRole)) {
        issues.push(`Unexpected string role: ${userData.role}`);
      }
    }
    
    if (typeof userData.role === 'number' && (userData.role < 0 || userData.role > 3)) {
      issues.push(`Role number out of valid range: ${userData.role}`);
    }
    
  } catch (error) {
    issues.push(`Failed to normalize role: ${error instanceof Error ? error.message : 'Unknown error'}`);
    normalizedRole = UserRole.EndUser;
  }
  
  return {
    isValid: issues.length === 0,
    normalizedRole,
    issues
  };
}

/**
 * Debug helper: Log role information
 */
export function debugRole(role: unknown, context: string = ''): void {
  const normalizedRole = normalizeRole(role);
  const roleInfo = ROLE_HIERARCHY[normalizedRole];
  
  console.group(`🔐 Role Debug${context ? ` - ${context}` : ''}`);
  console.log('Original Role:', role);
  console.log('Normalized Role:', normalizedRole);
  console.log('Role Name:', roleInfo.name);
  console.log('Display Name:', roleInfo.displayName);
  console.log('Level:', roleInfo.level);
  console.log('Permissions:', roleInfo.permissions);
  console.groupEnd();
}

// Export for testing and debugging
export { UserRole } from '@/types/api';